// Subscription · Wallet · Rewards · Service Bill — API layer (RM side).
//
// Every endpoint from the backend "Frontend Developer Guide" that the RM
// panel uses lives here, so screens never hard-code a URL. Same house
// pattern as autopayMandateApi.js: `store.dispatch(getData(...))` posts a
// FormData body and resolves to the backend envelope
// `{ status, success, data, message }` — also on non-2xx responses, because
// getData normalises them via rejectWithValue. Callers only ever branch on
// `res.success` and show `res.message` on failure.
//
// fee-ledger is the one POST that takes a QUERY STRING instead of a form
// body, so it goes through getPlainRequest (same as before).
import store from "../../../Redux/Store/Store";
import { getData } from "../../../Redux/Features/Slices/GetDataSlice";
import { getPlainRequest } from "../../../Redux/Features/Slices/GetPlainRequestSlice";

export const SUBSCRIPTION_ENDPOINTS = {
  flag: "Subscription/subscription-flag",
  serviceStatus: "Subscription/service-status",
  feeLedger: "Subscription/fee-ledger",
  invoice: "Subscription/invoice",
  cibilMonthly: "Subscription/cibil-monthly",
  payNow: "Subscription/pay-now",
  // Subscription/hold/* were removed by the backend (no Hold feature).
  walletTransactions: "Subscription/wallet-transactions",
  rewardLedger: "Subscription/reward-ledger",
  allocationCurrent: "Master/allocation/current",
  serviceBillCreate: "ServiceBill/create",
  serviceBillList: "ServiceBill/list",
  // No admin approval for service bills: the DSA types the bill's invoice
  // number (approve — old route name, it now only saves InvoiceNumber) and
  // its receipt details (receipt). Only on the DSA's own bills (403
  // otherwise). ServiceBill/reject was removed by the backend.
  serviceBillApprove: "ServiceBill/approve",
  serviceBillReceipt: "ServiceBill/receipt",
  // The lead's package rows (Buy Now and Resale) — FRONTEND_2 §E.
  leadSalePackages: "CamNoteDetail/GetSalePackagesByLeadId",
};

const NO_RESPONSE = { success: false, message: "No response from server" };

export const getEmployeeId = () => localStorage.getItem("ID");

// The guide asks for empty values to be left out of the form entirely
// (backend [FromForm] binding treats "" / "null" as a real value).
const cleanFields = (fields = {}) => {
  const out = {};
  Object.keys(fields).forEach((key) => {
    const value = fields[key];
    if (value !== undefined && value !== null && value !== "") out[key] = value;
  });
  return out;
};

const post = async (url, fields) => {
  try {
    const res = await store.dispatch(
      getData({ url, FormDataDynamic: cleanFields(fields) })
    );
    return res?.payload || NO_RESPONSE;
  } catch (error) {
    console.error(`${url} failed:`, error);
    return { success: false, message: "Something went wrong, try again" };
  }
};

// Write APIs (SP based) answer with `data: [{ Success, Message, Id? }]` —
// this pulls the first row out so callers can read Id / Message directly.
export const firstRow = (res) =>
  Array.isArray(res?.data) ? res.data[0] || null : res?.data || null;

export const asRows = (res) => (Array.isArray(res?.data) ? res.data : []);

// The text to show the RM for ANY response in the subscription module —
// always the backend's own words (envelope `message`, else the SP row's
// `Message`). The fallback is used only when the backend sent no text at
// all (e.g. network failure), never to replace a backend message.
export const apiMessage = (res, fallback = "Something went wrong, try again") =>
  res?.message || firstRow(res)?.Message || fallback;

// ---------------------------------------------------------------- S0 flag
export const fetchSubscriptionFlag = () => post(SUBSCRIPTION_ENDPOINTS.flag);

// ------------------------------------------------- S2 home / ledger / invoice
export const fetchServiceStatus = (employeeId = getEmployeeId()) =>
  post(SUBSCRIPTION_ENDPOINTS.serviceStatus, { employeeId });

export const fetchFeeLedger = async ({ employeeId = getEmployeeId(), month, year } = {}) => {
  const params = { EmployeeId: employeeId };
  if (month) params.ForMonth = Number(month);
  if (year) params.ForYear = Number(year);
  try {
    const res = await store.dispatch(
      getPlainRequest({
        url: SUBSCRIPTION_ENDPOINTS.feeLedger,
        params,
        key: "subscriptionFeeLedger",
      })
    );
    return res?.payload?.data || NO_RESPONSE;
  } catch (error) {
    console.error("fee-ledger failed:", error);
    return { success: false, message: "Failed to fetch ledger data" };
  }
};

export const fetchInvoice = ({ employeeId = getEmployeeId(), forMonth, forYear }) =>
  post(SUBSCRIPTION_ENDPOINTS.invoice, {
    EmployeeId: employeeId,
    ForMonth: forMonth,
    ForYear: forYear,
  });

export const fetchCibilMonthly = ({ employeeId = getEmployeeId(), month, year } = {}) =>
  post(SUBSCRIPTION_ENDPOINTS.cibilMonthly, {
    EmployeeId: employeeId,
    ForMonth: month,
    ForYear: year,
  });

// ---------------------------------------------------------------- S3 pay now
// Success only means the debit request was SENT — the bank callback lands
// 1–2 min later, so callers must refresh flag + ledger afterwards.
export const payNow = ({ employeeId = getEmployeeId(), forMonth, forYear }) =>
  post(SUBSCRIPTION_ENDPOINTS.payNow, { employeeId, forMonth, forYear });

// --------------------------------------------------------- S5 wallet/rewards
export const fetchWalletTransactions = ({ employeeId = getEmployeeId(), month, year } = {}) =>
  post(SUBSCRIPTION_ENDPOINTS.walletTransactions, {
    EmployeeId: employeeId,
    ForMonth: month,
    ForYear: year,
  });

export const fetchRewardLedger = ({ employeeId = getEmployeeId(), month, year } = {}) =>
  post(SUBSCRIPTION_ENDPOINTS.rewardLedger, {
    EmployeeId: employeeId,
    ForMonth: month,
    ForYear: year,
  });

export const fetchCurrentAllocation = (asOf) =>
  post(SUBSCRIPTION_ENDPOINTS.allocationCurrent, { AsOf: asOf });

// ---------------------------------------------------------- S6 service bill
// RESALE: in ONE backend transaction the points are taken, the PENDING bill
// is raised and the package is written as SUCCESS on the lead. Too few
// points → success:false with PointsAvailable / Shortfall (nothing taken).
// (ServiceBill/redeem is no longer called separately.)
export const createServiceBill = ({
  employeeId = getEmployeeId(),
  packageId,
  qty = 1,
  billType = "RESALE",
  leadRef,
  customerName,
  customerMobile,
  redemptionNo,
  note,
}) =>
  post(SUBSCRIPTION_ENDPOINTS.serviceBillCreate, {
    EmployeeId: employeeId,
    PackageId: packageId,
    Qty: qty,
    BillType: billType,
    LeadRef: leadRef,
    CustomerName: customerName,
    CustomerMobile: customerMobile,
    RedemptionNo: redemptionNo,
    Note: note,
  });

// LeadRef + PackageId narrow the list to one lead / package (Package tab:
// "already resold on this lead?"). Each row carries Stage
// (INVOICE_PENDING / RECEIPT_PENDING / PAID) and Balance.
export const fetchServiceBills = ({ employeeId = getEmployeeId(), id, leadRef, packageId } = {}) =>
  post(SUBSCRIPTION_ENDPOINTS.serviceBillList, {
    EmployeeId: employeeId,
    Id: id,
    LeadRef: leadRef,
    PackageId: packageId,
  });

// The lead's package rows. A RESALE row carries ServiceBillId, which links a
// service bill to its package row id (the ServiceId the invoice / receipt
// PDF routes need).
export const fetchLeadSalePackages = (leadId) =>
  post(SUBSCRIPTION_ENDPOINTS.leadSalePackages, { LeadId: leadId });

// Save / change the bill's invoice number, typed by the DSA (Service Bill
// Ledger → Invoice). Can be changed again until the bill is PAID.
export const saveServiceBillInvoice = ({ employeeId = getEmployeeId(), id, invoiceNumber }) =>
  post(SUBSCRIPTION_ENDPOINTS.serviceBillApprove, {
    Id: id,
    EmployeeId: employeeId,
    InvoiceNumber: invoiceNumber,
  });

// Payment received from the customer (Service Bill Ledger → Receipt). All
// four details are typed by the DSA; no invoice is needed first. A smaller
// PaidAmount keeps the bill PENDING with a Balance.
export const saveServiceBillReceipt = ({
  employeeId = getEmployeeId(),
  id,
  paidAmount,
  paidRef,
  receiptDate,
  receiptNumber,
}) =>
  post(SUBSCRIPTION_ENDPOINTS.serviceBillReceipt, {
    Id: id,
    EmployeeId: employeeId,
    PaidAmount: paidAmount,
    PaidRef: paidRef,
    ReceiptDate: receiptDate,
    ReceiptNumber: receiptNumber,
  });
